import React from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { Sidebar } from './Sidebar';
import { Breadcrumbs } from '../navigation/Breadcrumbs';
import { useNavigation } from '../../lib/hooks/useNavigation';
import { useAuth } from '../../lib/hooks/useAuth';
import { APP_CONSTANTS } from '../../lib/constants';
import { cn, styles } from '../../lib/utils/styles';
import type { UserRole } from '../../lib/types/shared';

export const Layout = React.memo(function Layout() {
  const { user, logout } = useAuth();
  const userRole = (user?.role || 'guest') as UserRole;
   const location = useLocation();

  const {
    isAuthorized,
    requiredRole,
    breadcrumbs,
  } = useNavigation(userRole, location.pathname);

  // Handle logout
  const handleLogout = React.useCallback(() => {
    logout().catch((error) => {
      console.error('Logout failed:', error);
    });
  }, [logout]);

  // Handle unauthorized access
  if (!isAuthorized) {
    return <Navigate to="/unauthorized" replace />;
  }

  return (
    <div className={styles.layout.flexRow}>
      {/* Sidebar */}
      <Sidebar
        userRole={userRole}
        onLogout={handleLogout}
      />

      {/* Main content */}
      <main
        className={cn(
          'flex-1 min-h-screen',
          'bg-gray-50 dark:bg-gray-900',
          'transition-all duration-200'
        )}
      >
        {/* Header */}
        <header
          className={cn(
            'sticky top-0 z-30',
            'h-16 px-4',
            'bg-white dark:bg-gray-800',
            'border-b border-gray-200 dark:border-gray-700',
            'flex items-center justify-between'
          )}
        >
          <Breadcrumbs items={breadcrumbs} />

          {/* Role indicator */}
          {requiredRole && (
            <div className="text-sm text-gray-500 dark:text-gray-400">
              This page requires {requiredRole} access
            </div>
          )}
        </header>

        {/* Page content */}
        <div
          className={cn(
            'p-4 md:p-6 lg:p-8',
            'max-w-7xl mx-auto'
          )}
        >
          <Outlet />
        </div>

        {/* Footer */}
        <footer
          className={cn(
            'h-12',
            'border-t border-gray-200 dark:border-gray-700',
            'flex items-center justify-center',
            'text-sm text-gray-500 dark:text-gray-400'
          )}
        >
          {APP_CONSTANTS.APP_NAME} v{APP_CONSTANTS.APP_VERSION}
        </footer>
      </main>
    </div>
  );
});

/**
 * Example usage in App.tsx:
 * 
 * <BrowserRouter>
 *   <Routes>
 *     {/* Public routes *\/}
 *     <Route path="/" element={<Landing />} />
 *     <Route path="/login" element={<AuthPage />} />
 *     <Route path="/register" element={<AuthPage isRegister />} />
 * 
 *     {/* Protected routes *\/}
 *     <Route element={<Layout />}>
 *       <Route path="/dashboard" element={<Dashboard />} />
 *       <Route path="/profile" element={<Profile />} />
 *       <Route path="/settings" element={<Settings />} />
 *     </Route>
 * 
 *     {/* Catch all *\/}
 *     <Route path="*" element={<Navigate to="/dashboard" replace />} />
 *   </Routes>
 * </BrowserRouter>
 */
